import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { MenuItem } from '@velasca/types'
import type { PublisherMenuImage } from '@velasca/types/src/Navbar'
import { ImageSkeleton } from '@velasca/ui/image-skeleton'
import { buildWidthSources } from '@velasca/utils/build-width-sources'
import { formatMenuTitle } from '@velasca/utils/format-menu-title'
import * as S from './MenuPage.styles'

interface ImageProps {
  src: string
  alt: string
  onError?: (e: any) => void
  onLoad?: (e: any) => void
}

const BannerImage = ({ src, alt, onError, onLoad }: ImageProps) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  if (!src) {
    return null
  }

  const { sources, sizes } = buildWidthSources(
    src,
    [
      { maxWidth: 375, imageWidth: 350 },
      { maxWidth: 390, imageWidth: 365 },
      { maxWidth: 430, imageWidth: 405 },
      { maxWidth: 768, imageWidth: 740 },
      { maxWidth: 820, imageWidth: 800 },
    ],
    1200,
  )

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true)
    onLoad?.(e)
  }

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true)
    onError?.(e)
  }

  if (imageError) {
    return null
  }

  return (
    <S.ImageWithSkeletonContainer>
      {!imageLoaded && <ImageSkeleton aspectRatio="4 / 5" />}
      <S.BannerImage
        lqip
        lazyload
        key={src}
        imgSrc={src}
        alt={alt}
        sizes={sizes}
        sources={sources}
        style={{
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        onError={handleError}
        onLoad={handleLoad}
      />
    </S.ImageWithSkeletonContainer>
  )
}

const Banner = ({ banner }: { banner: PublisherMenuImage }) => {
  const [disabled, setDisabled] = useState(false)

  if (!banner.src || disabled) {
    return null
  }

  return (
    <S.Link key={banner.src} href={banner.url ?? undefined}>
      <S.Banner>
        <S.BannerImageContainer>
          <BannerImage
            src={banner.src}
            alt={banner.title ?? banner.alt ?? ''}
            onError={() => {
              setDisabled(true)
            }}
          />
        </S.BannerImageContainer>
        <S.BannerTitle>{banner.title}</S.BannerTitle>
      </S.Banner>
    </S.Link>
  )
}

const MenuEntryImage = ({ src, alt, onError, onLoad }: ImageProps) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  if (!src) {
    return <S.EmptyMenuEntryImage />
  }

  const { sources, sizes } = buildWidthSources(
    src,
    [
      { maxWidth: 375, imageWidth: 165 },
      { maxWidth: 390, imageWidth: 175 },
      { maxWidth: 430, imageWidth: 195 },
      { maxWidth: 768, imageWidth: 365 },
      { maxWidth: 820, imageWidth: 380 },
    ],
    600,
  )

  const handleLoad = (e: any) => {
    setImageLoaded(true)
    onLoad?.(e)
  }

  const handleError = (e: any) => {
    setImageError(true)
    onError?.(e)
  }

  if (imageError) {
    return <S.EmptyMenuEntryImage />
  }

  return (
    <S.ImageWithSkeletonContainer>
      {!imageLoaded && <ImageSkeleton aspectRatio="4 / 5" />}
      <S.MenuEntryImage
        lqip
        lazyload
        key={src}
        imgSrc={src}
        alt={alt}
        sizes={sizes}
        sources={sources}
        style={{
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        onError={handleError}
        onLoad={handleLoad}
      />
    </S.ImageWithSkeletonContainer>
  )
}

interface MenuPageProps {
  menu: MenuItem[]
  menuItem?: MenuItem
  back?: () => void
  select: (item: MenuItem) => void
  banners?: PublisherMenuImage[]
  level: number
}

export const MenuPage = ({
  menu,
  menuItem,
  select,
  back,
  banners,
  level,
}: MenuPageProps) => {
  const { t } = useTranslation('header')
  const layout = level < 2 ? 'list' : 'grid'

  const hasMenuItemUrl = menuItem && menuItem.url !== '#'

  return (
    <S.MenuPageContainer>
      <S.MenuPageLinks>
        <S.MenuUtilityLinks>
          {back && (
            <S.MenuEntry back hideBorder key="back" onClick={back}>
              <S.BackArrow />
            </S.MenuEntry>
          )}
          {hasMenuItemUrl && (
            <S.MenuEntry
              href={menuItem.url}
              target={menuItem.isTargetBlank ? '_blank' : '_self'}
              key="link"
            >
              <S.MenuViewAllTitle>
                {menuItem.viewAllCopy ?? t('desktopViewAll')} <S.Arrow />
              </S.MenuViewAllTitle>
            </S.MenuEntry>
          )}
        </S.MenuUtilityLinks>
        <S.MenuItemsLinks layout={layout}>
          {menu?.map((item) => {
            const { sanitizedTitle, className } = formatMenuTitle(item.title)

            return (
              <S.MenuEntry
                key={item.linkHandle}
                layout={layout}
                href={!!item.url && item.url !== '#' ? item.url : undefined}
                target={item.isTargetBlank ? '_blank' : '_self'}
                onClick={(e) => {
                  if (item.children) {
                    e.preventDefault()
                    select(item)
                  }
                }}
              >
                {layout === 'grid' && (
                  <MenuEntryImage src={item.image ?? ''} alt={item.title} />
                )}
                <S.MenuTitle className={className} layout={layout}>
                  {sanitizedTitle}
                </S.MenuTitle>
                {layout !== 'grid' && <S.ArrowRight />}
              </S.MenuEntry>
            )
          })}
        </S.MenuItemsLinks>
      </S.MenuPageLinks>
      {banners?.map((banner) => <Banner key={banner.src} banner={banner} />)}
    </S.MenuPageContainer>
  )
}
